import { useEffect, useState } from "react";
import { Pause, Play, Check, Timer } from "lucide-react";

const STORE = "sp.timer.";
const fmt = (ms) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
};
const load = (key) => {
  try {
    return JSON.parse(localStorage.getItem(STORE + key) || "null");
  } catch {
    return null;
  }
};

/** Visual session timer: planned vs elapsed, pause / resume, keeps counting past the planned time. */
export const SessionTimerCard = ({ timerKey, title, plannedMinutes = 20, doneMinutes = 0, mobileType = false, onFinish }) => {
  const done = doneMinutes > 0;
  const [state, setState] = useState(() => load(timerKey) || { startedAt: Date.now(), pausedAt: null, pausedMs: 0 });
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (done) localStorage.removeItem(STORE + timerKey);
    else localStorage.setItem(STORE + timerKey, JSON.stringify(state));
  }, [timerKey, state, done]);
  useEffect(() => {
    if (done || state.pausedAt) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [done, state.pausedAt]);

  const end = state.endedAt || state.pausedAt || now;
  const elapsed = done && !state.endedAt ? doneMinutes * 60000 : end - state.startedAt - state.pausedMs;
  const planned = plannedMinutes * 60000;
  const over = elapsed > planned;
  const ratio = Math.min(1, elapsed / planned);
  const radius = 52;
  const circle = 2 * Math.PI * radius;
  const color = over ? "#F59E0B" : "#25D366";
  const text = mobileType ? "text-[15px]" : "text-[13px]";

  const pause = () => setState((s) => ({ ...s, pausedAt: Date.now() }));
  const resume = () => setState((s) => ({ ...s, pausedMs: s.pausedMs + (Date.now() - s.pausedAt), pausedAt: null }));
  const finish = () => {
    const at = state.pausedAt || Date.now();
    const next = { ...state, endedAt: at };
    setState(next);
    onFinish?.(Math.max(1, Math.round((at - state.startedAt - state.pausedMs) / 60000)));
  };

  return (
    <div className="w-[94%] sm:w-[88%] max-w-[23rem] self-start rounded-2xl rounded-tl-sm bg-white p-2.5 sm:p-3 shadow-sm" data-testid="session-timer-card">
      <div className="flex items-center gap-2">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#E9FBF0] text-[#189346]"><Timer className="h-4 w-4" /></span>
        <p className={`min-w-0 flex-1 truncate font-semibold text-[#122118] ${text}`}>{title}</p>
      </div>
      <div className="mt-3 flex items-center gap-3">
        <svg viewBox="0 0 120 120" className="h-24 w-24 shrink-0 -rotate-90" aria-hidden="true">
          <circle cx="60" cy="60" r={radius} fill="none" stroke="#EEF3F0" strokeWidth="10" />
          <circle cx="60" cy="60" r={radius} fill="none" stroke={color} strokeWidth="10" strokeLinecap="round" strokeDasharray={circle} strokeDashoffset={circle * (1 - ratio)} style={{ transition: "stroke-dashoffset 0.9s linear, stroke 0.3s" }} />
        </svg>
        <div className="min-w-0 flex-1">
          <p className="font-display text-3xl font-bold tabular-nums text-[#122118]" data-testid="session-timer-elapsed">{fmt(elapsed)}</p>
          <p className="whitespace-nowrap text-[12px] text-[#829C8D]" data-testid="session-timer-planned">Prévu : {plannedMinutes} min</p>
          <p className={`mt-1 text-[12px] font-semibold ${over ? "text-[#B45309]" : "text-[#189346]"}`} data-testid="session-timer-status">
            {done ? "Séance terminée" : over ? `Dépassé · +${fmt(elapsed - planned)}` : state.pausedAt ? "En pause" : `Encore ${fmt(planned - elapsed)}`}
          </p>
        </div>
      </div>
      {!done && (
        <div className="mt-3 flex gap-2">
          {state.pausedAt ? (
            <button type="button" onClick={resume} className={`inline-flex flex-1 items-center justify-center gap-1.5 rounded-full border border-gray-200 px-3 py-2 font-semibold text-[#2d5a3d] ${text}`} data-testid="session-timer-resume">
              <Play className="h-4 w-4" /> Continuer
            </button>
          ) : (
            <button type="button" onClick={pause} className={`inline-flex flex-1 items-center justify-center gap-1.5 rounded-full border border-gray-200 px-3 py-2 font-semibold text-[#2d5a3d] ${text}`} data-testid="session-timer-pause">
              <Pause className="h-4 w-4" /> Pause
            </button>
          )}
          <button type="button" onClick={finish} className={`inline-flex flex-1 items-center justify-center gap-1.5 rounded-full bg-[#25D366] px-3 py-2 font-semibold text-[#0a2914] ${text}`} data-testid="session-timer-finish">
            <Check className="h-4 w-4 shrink-0" /> Terminé
          </button>
        </div>
      )}
    </div>
  );
};

export default SessionTimerCard;
